<template>
  <div class="main-page page-foreign-pdf">
    <Menu :title="title" :home="true" />
    <div class="container-flex pdf-container">
      <div class="pdf-toolbar">
        <div class="document-label"><i class="el-icon-document"></i><span>文档预览<small>PDF DOCUMENT</small></span></div>
        <div class="page-controls">
          <button :disabled="loading || rendering || pageNo <= 1" @click="changePage(-1)"><i class="el-icon-arrow-left"></i> 上一页</button>
          <span class="page-counter"><strong>{{ pageNo }}</strong><span>/</span><span class="page-total">{{ pageCount || '—' }}</span><small>页</small></span>
          <button :disabled="loading || rendering || pageNo >= pageCount" @click="changePage(1)">下一页 <i class="el-icon-arrow-right"></i></button>
        </div>
        <el-popover v-model="showTakeaway" placement="bottom" trigger="click" :width="210">
          <div class="pdf-takeaway-code" style="text-align: center;">
            <img v-if="takeawayCode" :src="takeawayCode" alt="扫码获取当前 PDF 文件" style="display: block; width: 190px; height: 190px; margin: 0 auto;">
            <div v-else style="padding: 24px 0;">{{ takeawayError || '二维码生成中…' }}</div>
            <div style="padding: 8px 0 4px; color: #666; font-size: 14px;">手机扫码，带走当前文档</div>
          </div>
          <button slot="reference" class="takeaway-button" @click="generateTakeawayCode"><i class="el-icon-mobile-phone"></i> 扫码带走</button>
        </el-popover>
      </div>
      <div v-if="loading" class="pdf-state">正在加载文档…</div>
      <div v-else-if="error" class="pdf-state">{{ error }} <button @click="loadDocument">重试</button></div>
      <div ref="viewport" class="pdf-viewport"><canvas v-show="!loading && !error" ref="canvas"></canvas></div>
    </div>
  </div>
</template>

<script>
import Menu from '@/components/Menu'
import PDF from 'pdfjs-dist'
import QRCode from 'qrcode'

PDF.GlobalWorkerOptions.workerSrc = '/js/pdf.worker.min.js'

export default {
  components: { Menu },
  data () { return { loading: false, rendering: false, error: '', pageNo: 1, pageCount: 0, showTakeaway: false, takeawayCode: '', takeawayError: '' } },
  computed: {
    title () { return this.$route.query.title || '文件预览' },
    fileUrl () { return this.$api.getForeignLegalFileUrl(this.$route.params.fileId) }
  },
  mounted () { this.loadDocument() },
  watch: { fileUrl () { this.showTakeaway = false; this.takeawayCode = ''; this.takeawayError = ''; this.loadDocument() } },
  beforeDestroy () { this.releaseDocument() },
  methods: {
    async generateTakeawayCode () {
      if (this.takeawayCode) return
      const url = this.fileUrl
      this.takeawayError = ''
      try {
        const code = await QRCode.toDataURL(url, { width: 380, margin: 2, errorCorrectionLevel: 'M' })
        if (url === this.fileUrl) this.takeawayCode = code
      } catch (error) {
        if (url === this.fileUrl) this.takeawayError = '二维码生成失败，请再次点击重试'
      }
    },
    releaseDocument () {
      if (this.renderTask) { this.renderTask.cancel(); this.renderTask = null }
      if (this.loadingTask) { this.loadingTask.destroy(); this.loadingTask = null }
      this.pdfDocument = null
    },
    async loadDocument () {
      this.releaseDocument()
      this.loading = true
      this.error = ''
      this.pageNo = 1
      this.pageCount = 0
      const task = PDF.getDocument({ url: this.fileUrl, isEvalSupported: false })
      this.loadingTask = task
      try {
        const pdf = await task.promise
        if (this.loadingTask !== task) return
        this.pdfDocument = pdf
        this.pageCount = pdf.numPages
        this.loading = false
        await this.renderPage()
      } catch (error) {
        if (this.loadingTask === task) { this.error = '文档加载失败，请重试'; this.loading = false }
      }
    },
    async renderPage () {
      if (!this.pdfDocument) return
      this.rendering = true
      try {
        const page = await this.pdfDocument.getPage(this.pageNo)
        const canvas = this.$refs.canvas
        if (!canvas) return
        const initial = page.getViewport(1)
        const width = this.$refs.viewport.clientWidth - 40
        const viewport = page.getViewport(Math.min(width / initial.width, 2))
        const ratio = window.devicePixelRatio || 1
        canvas.width = Math.ceil(viewport.width * ratio)
        canvas.height = Math.ceil(viewport.height * ratio)
        canvas.style.width = viewport.width + 'px'
        canvas.style.height = viewport.height + 'px'
        this.renderTask = page.render({ canvasContext: canvas.getContext('2d'), viewport, transform: [ratio, 0, 0, ratio, 0, 0] })
        await this.renderTask.promise
        this.$refs.viewport.scrollTop = 0
      } catch (error) {
        if (error.name !== 'RenderingCancelledException' && this.$refs.canvas) this.error = '文档渲染失败，请重试'
      } finally { this.rendering = false }
    },
    changePage (offset) { this.pageNo += offset; this.renderPage() }
  }
}
</script>

<style lang="stylus" scoped>
.page-foreign-pdf
  .pdf-container
    padding 0.2rem 0.6rem 0.5rem
    display flex
    flex-direction column
    overflow hidden
  .pdf-toolbar
    display flex
    justify-content space-between
    align-items center
    > * + *
      margin-left 0.3rem
    padding 0.18rem 0.3rem
    background #fff
    border-radius 0.16rem 0.16rem 0 0
    border-bottom 1px solid #e5ebf3
    box-shadow 0 4px 16px rgba(24,48,87,.04)
    z-index 1
    .document-label
      display flex
      align-items center
      > i
        margin-right 0.12rem
      color #32445e
      font-size 0.19rem
      > i
        display flex
        align-items center
        justify-content center
        width 0.44rem
        height 0.44rem
        border-radius 0.1rem
        color #3276da
        background #edf4ff
        font-size 0.26rem
      small
        display block
        margin-top 0.035rem
        font-size 0.095rem
        letter-spacing 0.015rem
        color #94a1b3
    .page-controls
      display flex
      align-items center
      > * + *
        margin-left 0.18rem
    .page-counter
      display flex
      align-items baseline
      justify-content center
      > * + *
        margin-left 0.1rem
      min-width 1.2rem
      color #64748b
      font-size 0.18rem
      strong
        color #2366c8
        font-size 0.25rem
      > span
        color #c1cad6
      > .page-total
        color #64748b
      small
        font-size 0.14rem
    button
      min-height 0.46rem
      padding 0.1rem 0.2rem
      background #edf4ff
      border 1px solid #dbe8fc
      color #286aca
      border-radius 0.09rem
      font-size 0.18rem
      cursor pointer
      transition background .15s, box-shadow .15s
      &:hover:not(:disabled)
        background #e0edff
      &:focus-visible
        outline 2px solid #3276da
        outline-offset 3px
      &:disabled
        color #aab6c7
        background #f5f7fa
        border-color #edf0f4
        cursor default
    .takeaway-button
      border 0
      padding 0.1rem 0.24rem
      color #fff
      background linear-gradient(135deg, #ff9252, #ff6e43)
      box-shadow 0 4px 12px rgba(255,112,67,.2)
      &:hover
        background #f77942
  .pdf-state
    padding 0.3rem
    background #fff
    text-align center
  .pdf-viewport
    flex 1
    min-height 0
    overflow auto
    padding 0.3rem 0.35rem 0.5rem
    background #edf1f6
    border-radius 0 0 0.16rem 0.16rem
    scrollbar-color #bcc9db transparent
    &::-webkit-scrollbar
      width 8px
      height 8px
    &::-webkit-scrollbar-thumb
      border-radius 8px
      border 2px solid #edf1f6
      background #bcc9db
  canvas
    display block
    margin 0 auto
    background #fff
    box-shadow 0 6px 28px rgba(34,55,88,.12), 0 0 0 1px rgba(34,55,88,.04)
@media screen and (orientation: portrait)
  .page-foreign-pdf .pdf-toolbar
    flex-wrap wrap
    margin-bottom 0
    > *
      margin-bottom 0.15rem
    > * + *
      margin-left 0.15rem
    .page-controls
      margin-left 0
    .document-label
      display none
</style>
